// 🔧 request_event.js
// ✅ Purpose: Public event setup and plan selection page for Let's Go Vote
// ✅ Features:
// - Allows verified logged-in users to start a new event setup flow
// - Collects event details, contact info, voting type, and selected pricing plan
// - Saves event requests to /pendingEvents before checkout begins
// - Creates or signs in users during the request process
// - Redirects users to Stripe Checkout for secure payment
// - Supports email verification for newly created accounts
// - Starts the event approval and activation workflow after payment
// - Uses standard success/error status notifications
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { addDoc, collection, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from "../../firebase";
import { useAuth } from "../../auth_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import {
  VOTING_TYPE_OPTIONS,
  DEFAULT_VOTING_TYPE,
} from "../../utils/voting_types";
import { logger } from "../../utils/logger";
import { showStatus } from "../../utils/status_helpers";
import { EVENT_TYPES } from "../../constants/event_types";
import {
  ATTENDANCE_ESTIMATES,
  HOW_HEARD_OPTIONS,
} from "../../constants/request_intake_options";
import SeoHead from "../../components/seo_head";
import { PUBLIC_PAGE_SEO } from "../../constants/marketing_pages";
import {
  LGV_EVENT_PACKAGE,
  LGV_CORE_FEATURES,
  LGV_OPTIONAL_POWER_UPS,
  getDefaultPurchasedAddOns,
} from "../../constants/lgv_features";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

const RequestEvent = () => {

  const { user, authLoading } = useAuth();
  const location = useLocation();
  const addOnFromUrl = new URLSearchParams(location.search).get("addon");

  const initialSelectedAddOns = LGV_OPTIONAL_POWER_UPS.some(
    (addOn) => addOn.key === addOnFromUrl
  )
    ? [addOnFromUrl]
    : [];
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittedRequest, setSubmittedRequest] = useState(null);
  const [accountPassword, setAccountPassword] = useState("");
  const [checkoutRedirecting, setCheckoutRedirecting] = useState(
    () => sessionStorage.getItem("LGV_CHECKOUT_REDIRECTING") === "true"
  );
  const [checkoutCountdown, setCheckoutCountdown] = useState(3);
  const [requestForm, setRequestForm] = useState({
    name: "",
    eventType: "car_show",
    startDate: "",
    endDate: "",
    eventLocation: "",
    eventWebsite: "",
    contactName: user?.firstName || "",
    contactEmail: user?.email || "",
    contactPhone: user?.phone || "",
    description: "",
    votingType: DEFAULT_VOTING_TYPE,
    attendanceEstimate: "not_sure",
    howHeardAboutUs: "",    
    // 🧩 Core included Event Package features
    // ✅ Purpose:
    // - All operational features are included in the standard LGV Event Package
    // - These are not paid add-ons
    requestedModules: LGV_CORE_FEATURES.map((feature) => feature.key),

    // 💳 Optional Revenue Add-ons
    // ✅ Purpose:
    // - Stores optional revenue tools selected during initial event checkout
    // - These will later be purchased with the base Event Package
    selectedAddOns: initialSelectedAddOns,
  });

  const eventTypeOptions = [
    "Car Show",
    "Art Show",
    "Pet Contest",
    "Food Competition",
    "School Election",
    "Community Awards",
    "Fundraiser",
    "Silent Auction",
    "Talent Show",
    "Other",
  ];

  const {
    name,
    eventType,
    startDate,
    endDate,
    eventLocation,
    eventWebsite,
    contactName,
    contactEmail,
    contactPhone,
    description,
    votingType,
    attendanceEstimate,
    howHeardAboutUs,
  } = requestForm;

  const selectedPowerUps = LGV_OPTIONAL_POWER_UPS.filter((addOn) =>
    (requestForm.selectedAddOns || []).includes(addOn.key)
  );

  const selectedAddOnTotal = selectedPowerUps.reduce(
    (total, addOn) => total + Number(addOn.price || 0),
    0
  );

  const estimatedCheckoutTotal =
    Number(LGV_EVENT_PACKAGE.price || 0) + selectedAddOnTotal;
  

  const logCheckoutStep = (step, extra = {}) => {
    console.log("💳 Request Event Checkout Step:", {
      step,
      checkoutRedirecting,
      submitting,
      authLoading,
      hasUser: !!user?.uid,
      contactEmail,
      time: new Date().toISOString(),
      ...extra,
    });
  };

useEffect(() => {
    console.log("👀 Request Event Render State:", {
      checkoutRedirecting,
      submitting,
      authLoading,
      hasUser: !!user?.uid,
      contactEmail,
      path: window.location.pathname,
      time: new Date().toISOString(),
    });
  }, [checkoutRedirecting, submitting, authLoading, user?.uid, contactEmail]);

  // 💳 Firebase Functions
  // ✅ Purpose:
  // - Gives this page access to callable Cloud Functions
  // - Used to start the Stripe Checkout flow after the event setup record is saved
  const functions = getFunctions();

  /* =====================================================
    💳 startCheckoutForEventRequest
    Purpose:
    - Calls a Firebase Callable Function that will create a Stripe Checkout Session
    - Expects the function to return a Stripe-hosted checkout URL
    - Keeps React away from Stripe secret keys
    - Falls back gracefully while Stripe is not configured yet
  ===================================================== */
  const startCheckoutForEventRequest = async (requestId) => {
    const createEventCheckoutSession = httpsCallable(
      functions,
      "createEventCheckoutSession"
    );

    const result = await createEventCheckoutSession({
      requestId,
      selectedPackage: LGV_EVENT_PACKAGE.key,
      selectedAddOns: requestForm.selectedAddOns || [],
    });

    const checkoutUrl = result?.data?.url;

    if (!checkoutUrl) {
      throw new Error("Checkout URL was not returned.");
    }

    return checkoutUrl;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setRequestForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddOnToggle = (addOnKey) => {
    setRequestForm((prev) => {
      const current = Array.isArray(prev.selectedAddOns)
        ? prev.selectedAddOns
        : [];

      const alreadySelected = current.includes(addOnKey);

      return {
        ...prev,
        selectedAddOns: alreadySelected
          ? current.filter((key) => key !== addOnKey)
          : [...current, addOnKey],
      };
    });
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    sessionStorage.setItem("LGV_CHECKOUT_REDIRECTING", "true");
    setCheckoutRedirecting(true);
    logCheckoutStep("submit_clicked_overlay_enabled");
    setCheckoutCountdown(3);

    if (
      !name ||
      !eventType ||
      !startDate ||
      !eventLocation ||
      !contactName ||
      !contactEmail ||
      !description
    ) {
      sessionStorage.removeItem("LGV_CHECKOUT_REDIRECTING");
      setCheckoutRedirecting(false);

      showStatus(setStatus, {
        type: "error",
        message: "Please complete all required fields.",
      });

      return;
    }

    logCheckoutStep("before_ensure_request_user");
    const accountResult = await ensureRequestUser();

    logCheckoutStep("after_ensure_request_user", {
      activeUid: accountResult?.firebaseUser?.uid || "",
      createdNewUser: accountResult?.createdNewUser === true,
    });

    if (!accountResult?.firebaseUser?.uid) {
      sessionStorage.removeItem("LGV_CHECKOUT_REDIRECTING");
      setCheckoutRedirecting(false);
      return;
    }

    const activeUser = accountResult.firebaseUser;
    const createdNewUser = accountResult.createdNewUser;

    try {
      setSubmitting(true);

      const pendingEventPayload = {
        name,
        eventType,
        description,
        startDate: new Date(startDate),
        endDate: endDate ? new Date(endDate) : null,
        eventLocation,
        attendanceEstimate,
        howHeardAboutUs,
        eventWebsite,
        reviewStatus: "pending",
        pending: true,
        archived: false,
        createdAt: serverTimestamp(),
        submittedBy: {
          uid: activeUser.uid || "",
          email: activeUser.email || contactEmail || "",
          firstName: contactName.split(" ")[0] || "",
          lastName: contactName.split(" ").slice(1).join(" ") || "",
          phone: contactPhone || "",
        },
        createdNewUserDuringRequest: createdNewUser,
        emailVerificationRequired: createdNewUser,        
        requestedModules: LGV_CORE_FEATURES.map((feature) => feature.key),
        selectedAddOns: requestForm.selectedAddOns || [],
        purchasedAddOns: getDefaultPurchasedAddOns(),
        eventContact: {
          name: contactName || "",
          email: contactEmail || "",
          phone: contactPhone || "",
        },
        // ✅ Backward compatibility for Dashboard pending request lookup
        requester: {
          uid: activeUser.uid || "",
          name: contactName || "",
          firstName: contactName.split(" ")[0] || "",
          lastName: contactName.split(" ").slice(1).join(" ") || "",
          email: activeUser.email || contactEmail || "",
          phone: contactPhone || "",
        },
        votingType: votingType || DEFAULT_VOTING_TYPE,
        // 💵 Selected Plan / Future Checkout Details
        // Purpose:
        // - Stores the desired payment tier on the pending event request
        // - Keeps Phase 1 compatible with the current manual review process
        // - Gives Stripe Phase 2/3 the plan details needed to start checkout
        selectedPackage: LGV_EVENT_PACKAGE.key,
        selectedPackageName: LGV_EVENT_PACKAGE.name,
        selectedPackagePriceDisplay: LGV_EVENT_PACKAGE.priceDisplay,
        selectedPackagePriceAmount: LGV_EVENT_PACKAGE.price,
        selectedAddOnKeys: requestForm.selectedAddOns || [],
        selectedAddOnNames: selectedPowerUps.map((addOn) => addOn.name),
        selectedAddOnTotal,
        estimatedCheckoutTotal,
        checkoutStatus: "not_started",
        paymentStatus: "not_paid",        
      };

      logCheckoutStep("before_pending_event_add");

      // ✅ Save event setup record first
      // Purpose:
      // - Keeps a Firestore record before payment starts
      // - Gives Stripe metadata a stable requestId to reference later
      // - Allows recovery if the user closes checkout before paying
      const pendingEventRef = await addDoc(
        collection(db, "pendingEvents"),
        pendingEventPayload
      );

      const pendingEventId = pendingEventRef.id;

      logCheckoutStep("after_pending_event_add", {
        pendingEventId,
      });

      // 📊 Google Analytics
      trackEvent({
        action: GA_EVENTS.REQUEST_EVENT_SUBMITTED,
        parameters: {
          event_type: eventType,
          voting_type: votingType,
          attendance: attendanceEstimate,
          add_on_count: (requestForm.selectedAddOns || []).length,
          estimated_total: estimatedCheckoutTotal,
        },
      });

      // 💳 Checkout Handoff
      // Purpose:
      // - Attempts to redirect the user to Stripe Checkout
      // - If Stripe returns an error, records the failure and prevents losing the event request
      try {

        logCheckoutStep("before_create_checkout_session", {
          pendingEventId,
        });

        const checkoutUrl = await startCheckoutForEventRequest(pendingEventId);

        logCheckoutStep("after_create_checkout_session", {
          hasCheckoutUrl: !!checkoutUrl,
        });

        // 🚀 Redirect immediately after Stripe URL is ready.
        // ✅ Do not await another Firestore write here because it causes the form to flash back before navigation.
        logCheckoutStep("before_redirect_timer_started", {
          checkoutUrl,
        });

        setTimeout(() => {
          logCheckoutStep("before_window_location_assign", {
            checkoutUrl,
          });

          sessionStorage.removeItem("LGV_CHECKOUT_REDIRECTING");
          window.location.assign(checkoutUrl);
        }, 3000);

        return;

      } catch (checkoutErr) {

        /* =====================================================
          💳 Checkout Failure Diagnostics
          Purpose:
          - Preserve the user-friendly checkout failure message
          - Log Firebase callable details for local troubleshooting
          - Never expose Stripe secrets or sensitive payment data
          - Helps distinguish authentication, ownership, Firestore,
            emulator-routing, and Stripe failures
        ===================================================== */

        console.error(
          "❌ LGV Event Checkout Failed:",
          {
            code:
              checkoutErr?.code || "",

            message:
              checkoutErr?.message || "",

            details:
              checkoutErr?.details || null,

            name:
              checkoutErr?.name || "",

            pendingEventId:
              pendingEventId || "",

            activeUid:
              activeUser?.uid || "",
          }
        );

        sessionStorage.removeItem(
          "LGV_CHECKOUT_REDIRECTING"
        );

        setCheckoutRedirecting(false);

        showStatus(setStatus, {
          type: "error",
          message:
            "Unable to start checkout. Please try again or contact support.",
        });

        return;
      }

      logCheckoutStep("after_ensure_request_user", {
        activeUid: accountResult.firebaseUser.uid,
        createdNewUser: accountResult.createdNewUser,
      });

      // ✅ Save a user-readable copy so the confirmation can show what was submitted
      setSubmittedRequest({
        ...pendingEventPayload,
        startDate,
        endDate,
      });

      setRequestForm({
        name: "",
        startDate: "",
        endDate: "",
        eventLocation: "",
        eventWebsite: "",
        contactName: user?.firstName || "",
        contactEmail: user?.email || "",
        contactPhone: user?.phone || "",
        description: "",
        votingType: DEFAULT_VOTING_TYPE,
        eventType,
        attendanceEstimate: "not_sure",
        howHeardAboutUs: "",
        requestedModules: LGV_CORE_FEATURES.map((feature) => feature.key),
        selectedAddOns: initialSelectedAddOns,
      });

      showStatus(setStatus, {
        type: "warning",
        message:
          "Your event request was saved, but checkout could not be started. Please contact support if this issue continues.",
      });
    } catch (err) {
      logger.error("❌ Failed to submit event request:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to submit your event setup. Please try again.",
      });
    } finally {
      console.log("🔥 FINALLY BLOCK RUNNING", {
        checkoutRedirecting,
        submitting,
        time: new Date().toISOString(),
      });

      if (sessionStorage.getItem("LGV_CHECKOUT_REDIRECTING") !== "true") {
        setSubmitting(false);
      }
    }
  };

    const ensureRequestUser = async () => {
      logCheckoutStep("ensure_request_user_started");
      if (user?.uid) {
      return {
        firebaseUser: user,
        createdNewUser: false,
      };
    }

    const cleanEmail = String(contactEmail || "").trim().toLowerCase();

    if (!cleanEmail) {
      showStatus(setStatus, {
        type: "error",
        message: "Please enter your contact email before continuing.",
      });

      return null;
    }

    if (!accountPassword || accountPassword.length < 6) {
      showStatus(setStatus, {
        type: "error",
        message: "Please enter a password with at least 6 characters.",
      });

      return null;
    }

    try {
      // 🔐 First try to sign in
      try {
        logCheckoutStep("before_password_login_attempt");
        const credential = await signInWithEmailAndPassword(
          auth,
          cleanEmail,
          accountPassword
        );

        logCheckoutStep("password_login_success", {
          uid: credential.user.uid,
        });

        return {
          firebaseUser: credential.user,
          createdNewUser: false,
        };
      } catch (loginError) {
        logCheckoutStep("password_login_failed_trying_create", {
          code: loginError?.code,
          message: loginError?.message,
        });

        logger.log("ℹ️ Login failed, attempting account creation", {});
      }

      // 🆕 If login failed, create account
      const credential = await createUserWithEmailAndPassword(
        auth,
        cleanEmail,
        accountPassword
      );

      logCheckoutStep("new_account_created", {
        uid: credential.user.uid,
      });

      await sendEmailVerification(credential.user);

      const nameParts = String(contactName || "").trim().split(" ");

      await setDoc(
        doc(db, "users", credential.user.uid),
        {
          email: cleanEmail,
          firstName: nameParts[0] || "",
          lastName: nameParts.slice(1).join(" ") || "",
          phone: contactPhone || "",
          role: "voter",
          userType: "registered",
          emailVerified: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      return {
        firebaseUser: credential.user,
        createdNewUser: true,
      };
    } catch (err) {
      logger.error("❌ Account handoff failed:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message:
          "Unable to log in or create an account. Please verify your email and password and try again.",
      });

      return null;
    }
  };

  // ✅ Required field validation for enabling checkout button
  const isFormValid =
    !!name &&
    !!eventType &&
    !!startDate &&
    !!eventLocation &&
    !!contactName &&
    !!contactEmail &&
    !!description;

  const isPasswordValid =
    user?.uid ||
    (accountPassword && accountPassword.trim().length >= 6);

  const canSubmit =
    isFormValid &&
    isPasswordValid &&
    !submitting;
    
  if (authLoading && !checkoutRedirecting) {
    return <FullPageSpinner message="Loading request form..." />;
  }

  return (
    <>
    <SeoHead
      {...PUBLIC_PAGE_SEO.requestEvent}
      breadcrumbs={[
        { name: "Home", path: "/" },
        {
          name: "Start Your Event",
          path: "/request_event",
        },
      ]}
    />

    {checkoutRedirecting && (
      <div
        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
        style={{
          background: "rgba(255, 255, 255, 0.88)",
          zIndex: 9999,
        }}
      >
        <div className="card shadow border-0 text-center p-4" style={{ maxWidth: "420px" }}>
          <div className="spinner-border text-primary mx-auto mb-3" role="status" />

          <h4 className="fw-bold mb-2">Starting Secure Checkout</h4>

          <p className="text-muted mb-2">
            We are preparing your Stripe checkout session.
          </p>

          <p className="fw-bold mb-0">
            Redirecting in a few seconds...
          </p>
        </div>
      </div>
    )}

    <section id="top-content" className="container-fluid py-5">
      <div className="container">

        {/* =====================================================
            🧭 Page Header
            Purpose:
            - Matches standard Let's Go Vote page layout
            - Gives the page a consistent title/subtitle area
        ===================================================== */}
        <div className="row mb-4">
          <div className="col text-start">
            <h1 className="display-5">🚀 Start Your Event</h1>
            <p className="lead text-muted">
              Start with the LGV Event Package, then choose any optional Power Ups your event needs.
            </p>
          </div>
        </div>

        {/* =====================================================
            📦 Main Content Card
            Purpose:
            - Standard white-section container used across app pages
            - Keeps form content visually consistent with dashboard/settings/admin pages
        ===================================================== */}
        <div className="white-section p-4 rounded shadow-sm bg-white">

          {/* =====================================================
              🔔 Status Notification
              Purpose:
              - Shows success/error/warning messages at the top of page content
              - Uses existing Let's Go Vote status style pattern
          ===================================================== */}
          {status?.message && (
            <div
              className={`alert ${
                status.type === "success"
                  ? "alert-success"
                  : status.type === "warning"
                  ? "alert-warning"
                  : status.type === "error"
                  ? "alert-danger"
                  : "alert-info"
              }`}
              role="alert"
            >
              {status.message}
            </div>
          )}

          {submittedRequest && (
            <div className="alert alert-success text-start">
              <h5 className="mb-3">✅ Event Request Submitted</h5>

              <p className="mb-3">
                Here is the event setup information we received. Stripe Checkout will be connected in the next phase.
              </p>

              <div className="table-responsive">
                <table className="table table-sm table-bordered mb-0">
                  <tbody>
                    <tr>
                      <th>Selected Package</th>
                      <td>
                        {submittedRequest.selectedPackageName || "LGV Event Package"}
                        <br />
                        <span className="text-muted">
                          {submittedRequest.selectedPackagePriceDisplay || "$99"}
                        </span>

                        {Array.isArray(submittedRequest.selectedAddOnNames) &&
                          submittedRequest.selectedAddOnNames.length > 0 && (
                            <div className="mt-2">
                              <strong>Add-ons:</strong>
                              <br />
                              {submittedRequest.selectedAddOnNames.join(", ")}
                            </div>
                          )}
                      </td>
                    </tr>
                    <tr>
                      <th>Checkout Status</th>
                      <td>
                        {submittedRequest.checkoutStatus || "Not started"}
                      </td>
                    </tr>
                    <tr>
                      <th style={{ width: "220px" }}>Event Name</th>
                      <td>{submittedRequest.name}</td>
                    </tr>

                    <tr>
                      <th>Event Type</th>
                      <td>{submittedRequest.eventType}</td>
                    </tr>

                    <tr>
                      <th>Voting Type</th>
                      <td>{submittedRequest.votingType}</td>
                    </tr>

                    <tr>
                      <th>Event Location</th>
                      <td>{submittedRequest.eventLocation}</td>
                    </tr>

                    <tr>
                      <th>Start Date</th>
                      <td>{submittedRequest.startDate || "Not provided"}</td>
                    </tr>

                    <tr>
                      <th>End Date</th>
                      <td>{submittedRequest.endDate || "Not provided"}</td>
                    </tr>

                    <tr>
                      <th>Event Web Page</th>
                      <td>{submittedRequest.eventWebsite || "Not provided"}</td>
                    </tr>

                    <tr>
                      <th>Event Contact</th>
                      <td>
                        {submittedRequest.eventContact?.name}
                        <br />
                        {submittedRequest.eventContact?.email}
                        <br />
                        {submittedRequest.eventContact?.phone || "No phone provided"}
                      </td>
                    </tr>

                    <tr>
                      <th>Description</th>
                      <td>{submittedRequest.description}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* =====================================================
              📝 Event Request Form
              Purpose:
              // Collects event setup and selected plan details
              // Temporarily saves setup records to /pendingEvents
              // Prepares this flow for Stripe Checkout and automatic event activation
          ===================================================== */}
          {true && (
            <form onSubmit={handleRequestSubmit} className="text-start">

              <div className="alert alert-info mb-4">
                <strong>How this works:</strong>
                <br />
                Choose your event package, select any optional Power Ups, and complete your event setup details.
                When you continue, you will be redirected to Stripe to complete secure checkout.
                After payment, your event setup will continue through the approval and activation process.
              </div>

              {/* =====================================================
                  💵 Event Package + Add-ons
                  Purpose:
                  - Shows the single LGV Event Package
                  - Lets customers select optional revenue add-ons during checkout
                  - Removes Starter/Professional/Premium tier confusion
              ===================================================== */}
              <div className="mb-4">
                <h4 className="fw-bold mb-2">
                  {LGV_EVENT_PACKAGE.name}
                </h4>

                <p className="text-muted">
                  One complete event package for voting and event operations. Add optional Power Ups
                  only if your event needs them.
                </p>

                <div className="card border-success border-3 shadow-sm mb-4" style={{ background:"#f0fff4" }}>
                  <div className="card-body">
                    <div className="row g-4 align-items-center">
                      <div className="col-12 col-lg-4">
                        <div className="badge bg-success mb-2">
                          Core Event Package
                        </div>

                        <h5 className="fw-bold">
                          {LGV_EVENT_PACKAGE.name}
                        </h5>

                        <div className="display-6 fw-bold text-success">
                          {LGV_EVENT_PACKAGE.priceDisplay}
                        </div>

                        <div className="text-muted">
                          {LGV_EVENT_PACKAGE.priceNote}
                        </div>
                      </div>

                      <div className="col-12 col-lg-8">
                        <div className="row g-2">
                          {LGV_CORE_FEATURES.map((feature) => (
                            <div className="col-12 col-md-6" key={feature.key}>
                              <div className="small">
                                <span className="text-success fw-bold">✓</span>{" "}
                                {feature.icon} {feature.name}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <h5 className="fw-bold mb-2">
                  Optional Power Ups
                </h5>

                <p className="text-muted">
                  Add event commerce, attendee engagement, and social promotion tools when you need them.
                </p>

                <div className="row g-3">
                  {LGV_OPTIONAL_POWER_UPS.map((addOn) => {
                    const isSelected = (requestForm.selectedAddOns || []).includes(addOn.key);

                    return (
                      <div className="col-12 col-md-6 col-xl" key={addOn.key}>
                        <button
                          type="button"
                          aria-pressed={isSelected}
                          className={`card h-100 w-100 text-start shadow-sm ${
                            isSelected ? "border-success border-3" : "border"
                          }`}
                          style={{ background: isSelected ? "#f0fff4" : "#ffffff" }}
                          onClick={() => handleAddOnToggle(addOn.key)}
                        >
                          <div className="card-body d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start gap-2">
                              <div className="display-6">
                                {addOn.icon}
                              </div>

                              {isSelected && (
                                <span className="badge bg-success">
                                  Selected
                                </span>
                              )}
                            </div>

                            <h6 className="fw-bold mt-2">
                              {addOn.name}
                            </h6>

                            <div className="fs-4 fw-bold text-success">
                              {addOn.priceDisplay}
                            </div>

                            <p className="small text-muted mb-0">
                              {addOn.description}
                            </p>
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="alert border-success border-3 mt-4 mb-0" role="status" aria-label="Estimated checkout total" style={{ background:"#f0fff4" }}>
                  <div className="d-flex flex-column flex-md-row justify-content-between gap-2">
                    <div>
                      <strong>Estimated Checkout Total</strong>
                      <div className="small text-muted">
                        Event Package {LGV_EVENT_PACKAGE.priceDisplay}
                        {selectedPowerUps.length > 0 &&
                          ` + ${selectedPowerUps.length} add-on(s)`}
                      </div>
                    </div>

                    <div className="fs-4 fw-bold text-success">
                      ${estimatedCheckoutTotal}
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Event Name*</label>
                  <input
                    name="name"
                    className="form-control"
                    value={requestForm.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Event Type *
                  </label>

                  <select
                    name="eventType"
                    className="form-select"
                    value={requestForm.eventType}
                    onChange={handleChange}
                  >
                    {EVENT_TYPES.map((type) => (
                      <option
                        key={type.value}
                        value={type.value}
                      >
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Voting Type*</label>
                  <select
                    name="votingType"
                    className="form-select"
                    value={requestForm.votingType || DEFAULT_VOTING_TYPE}
                    onChange={handleChange}
                    required
                  >
                    {VOTING_TYPE_OPTIONS.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <div className="form-text">
                    This controls the voting style your event will start with. You can adjust event settings later.
                  </div>
                </div>
                

                <div className="col-md-6">
                  <label className="form-label">Event Location*</label>
                  <input
                    name="eventLocation"
                    className="form-control"
                    value={requestForm.eventLocation}
                    onChange={handleChange}
                    placeholder="Venue, city, or city/state"
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Start Date*</label>
                  <input
                    type="date"
                    name="startDate"
                    className="form-control"
                    value={requestForm.startDate}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    className="form-control"
                    value={requestForm.endDate}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Estimated Attendance</label>

                  <select
                    name="attendanceEstimate"
                    className="form-select"
                    value={requestForm.attendanceEstimate || "not_sure"}
                    onChange={handleChange}
                  >
                    {ATTENDANCE_ESTIMATES.map((estimate) => (
                      <option key={estimate.value} value={estimate.value}>
                        {estimate.label}
                      </option>
                    ))}
                  </select>

                  <div className="form-text">
                    A rough estimate is fine.
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label">How did you hear about us?</label>

                  <select
                    name="howHeardAboutUs"
                    className="form-select"
                    value={requestForm.howHeardAboutUs || ""}
                    onChange={handleChange}
                  >
                    <option value="">Select one</option>

                    {HOW_HEARD_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Event Website</label>
                  <input
                    name="eventWebsite"
                    className="form-control"
                    value={requestForm.eventWebsite}
                    onChange={handleChange}
                    placeholder="https://example.com"
                  />
                  <div className="form-text">
                    Optional, but helpful if your event already has a website or social page.
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Event Manager Name*</label>
                  <input
                    name="contactName"
                    className="form-control"
                    value={requestForm.contactName}
                    onChange={handleChange}
                    required
                  />
                  <div className="form-text">
                    This may be you, or the person responsible for the event.
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Event Manager Email*</label>
                  <input
                    type="email"
                    name="contactEmail"
                    className="form-control"
                    value={requestForm.contactEmail}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Event Manager Phone</label>
                  <input
                    name="contactPhone"
                    className="form-control"
                    value={requestForm.contactPhone}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">Description*</label>
                  <textarea
                    name="description"
                    className="form-control"
                    rows={4}
                    value={requestForm.description}
                    onChange={handleChange}
                    required
                  />
                  <div className="form-text">
                    Tell us all about your event: its purpose, audience, activities, location, and how you plan to use voting. This helps us set up a better experience and draft accurate SEO content.
                  </div>
                </div>
              </div>

              {/* =====================================================
                  🔒 Stripe Checkout Notice
                  Purpose:
                  - Explains the checkout flow before payment starts
                  - Increases trust and reduces checkout abandonment
                  - Sets expectations that the user leaves LGV temporarily
              ===================================================== */}
              {true && (
                <div className="alert alert-success mt-4 mb-3">
                  <div className="fw-bold mb-2">
                    🔒 Secure Payment Processing
                  </div>

                  <p className="mb-2">
                    After clicking <strong>Continue Toward Checkout</strong>, you will be
                    redirected to <strong>Stripe</strong>, our secure payment processor,
                    to complete your purchase.
                  </p>

                  <p className="mb-0">
                    Once payment is completed, Stripe will automatically return you to
                    Let's Go Vote where you will receive confirmation of your purchase
                    and next steps for event approval and setup.
                  </p>
                </div>
              )}

              {!user?.uid && (
                <div className="alert alert-warning border mt-4">
                  <h5 className="fw-bold mb-2">Account Password Required</h5>

                  <p className="mb-3">
                    Please enter the LGV account password for{" "}
                    <strong>{contactEmail || "your email address"}</strong> to complete your
                    checkout process.
                    <br />
                    <br />
                    If this email does not already have an LGV account, enter your desired
                    password and a new account will be created for you automatically.
                  </p>

                  <input
                    type="password"
                    className="form-control"
                    value={accountPassword}
                    onChange={(e) => setAccountPassword(e.target.value)}
                    placeholder="LGV account password"
                    required
                  />

                  <div className="form-text">
                    You will be able to complete checkout after submitting this form. If a new
                    account is created, normal email verification will still be required before
                    you can access your dashboard or manage your event.
                  </div>
                </div>
              )}   

            <div className="mt-4">

              {!canSubmit && (
                <div className="text-muted small mb-2">
                  Complete all required fields
                  {!user?.uid ? " and provide a password" : ""}
                  {" "}to continue to checkout.
                </div>
              )}

              <div className="d-flex flex-column flex-md-row gap-2">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!canSubmit}
                >
                  {submitting
                    ? "Submitting..."
                    : "💳 Continue Toward Checkout"}
                </button>

              </div>
            </div>
            </form>
          )}
        </div>
      </div>
    </section>
    </>
  );
};

export default RequestEvent;
